import { useEffect, useState } from "react"
import styles from "./Loading.module.css"
import { useSearchParams } from 'react-router-dom';
import { updateLevelTermConnection, updateBookSearchString } from '../features/database/syncFunctions.js';
import { useDispatch, useSelector } from 'react-redux';
import { refreshToken } from "../features/users/login.js"
import { setLanguage } from "../features/language/language.js"
import { SaveBadges } from "../features/database/badges.js"
import { SaveTerms } from "../features/database/terms.js"
import { GetAllLogin } from "../features/database/login.js"
import { SaveText } from "../features/database/uiTexts.js"
import { SaveBooks } from "../features/database/booksTable.js"
import setGeolocation from '../features/geolocation/geolocation.js';

export default function Loading() {
    const [fetchDataState, SetFetchDataState] = useState(0);
    const [languageToQuery, SetLanguageToQueryery] = useState(undefined);
    const [error, SetErrors] = useState("");
    const [debug, SetDebug] = useState("");
    const [refreshed, SetRefreshed] = useState(false);
    const [searchParams] = useSearchParams();
    const dispatch = useDispatch();
    const language = useSelector((state) => state.language.language);

    useEffect(() => {
        if (!dispatch) return;
        const localLanguage = localStorage.getItem('language');
        if (localLanguage === "null" || localLanguage === null || !localLanguage) {
            localStorage.setItem("language", language);
        } else if (localLanguage !== language) {
            dispatch(setLanguage(localLanguage));
        }
    }, [dispatch, language]);

    useEffect(() => {
        SetFetchDataState(0);
        SetLanguageToQueryery(language);
    }, [language])

    useEffect(() => {
        const timer = setTimeout(() => {
            const login = GetAllLogin();
            if (refreshed === false && dispatch && login.refresh_token) {
                SetRefreshed(true);
                refreshToken(dispatch, login.refresh_token, (ok) => {
                    if (ok) {
                        console.log("token refershed");
                    }
                }, login.activeUserId)
            }
        }, 100);
        return () => {
            clearTimeout(timer);
        };
    }, [dispatch, refreshed])

    useEffect(() => {
        const fetchJsonWithTimeout = async (url, timeoutMs = 15000) => {
            const controller = new AbortController();
            const timer = setTimeout(() => controller.abort(), timeoutMs);
            try {
                const response = await fetch(url, { signal: controller.signal });
                if (!response.ok) {
                    const message = `Request failed (${response.status}) for ${url}`;
                    throw new Error(message);
                }
                return await response.json();
            } finally {
                clearTimeout(timer);
            }
        };

        const fetchJsonWithRetry = async (url, attempts = 2, timeoutMs = 15000) => {
            let lastError = null;
            for (let attempt = 0; attempt < attempts; attempt += 1) {
                try {
                    return await fetchJsonWithTimeout(url, timeoutMs);
                } catch (error) {
                    lastError = error;
                    const backoffMs = 500 * (attempt + 1);
                    await new Promise((resolve) => setTimeout(resolve, backoffMs));
                }
            }
            throw lastError;
        };

        const fetchData = async (languageToQuery) => {
            try {
                const baseUrl = process.env.REACT_APP_APIBASE;
                const uiTextsUrl = baseUrl + "mobile/uiTexts?locale=" + languageToQuery;
                const booksUrl = baseUrl + "mobile/v2/books?locale=" + languageToQuery;
                const termsUrl = baseUrl + "mobile/terms?locale=" + languageToQuery;
                const badgesUrl = baseUrl + "mobile/badges?locale=" + languageToQuery;

                const [
                    dataText,
                    dataBooks,
                    dataTerms,
                    dataBadges,
                ] = await Promise.all([
                    fetchJsonWithTimeout(uiTextsUrl),
                    fetchJsonWithRetry(booksUrl, 3, 20000),
                    fetchJsonWithTimeout(termsUrl),
                    fetchJsonWithTimeout(badgesUrl),
                ]);

                const buildMediaUrl = (media, versionKey) => {
                    if (!media?.url || !media?.versions || !media?.extension) return null;
                    const version = media.versions[versionKey];
                    if (!version) return null;
                    return `${media.url}${version}.${media.extension}`;
                };

                const normalizeMedia = (media) => {
                    if (!media || !media.url || !media.versions || !media.extension) return media;
                    return {
                        ...media,
                        original_s: media.original_s ?? buildMediaUrl(media, "original_s") ?? buildMediaUrl(media, "original_256") ?? buildMediaUrl(media, "binary_256"),
                        original_xl: media.original_xl ?? buildMediaUrl(media, "original_xl") ?? buildMediaUrl(media, "original_512") ?? buildMediaUrl(media, "binary_512"),
                        optimal: media.optimal ?? buildMediaUrl(media, "optimal") ?? buildMediaUrl(media, "original_4k"),
                    };
                };

                const normalizedBooks = dataBooks?.result?.list?.map((book) => ({
                    ...book,
                    coverUrls: normalizeMedia(book.coverUrls),
                    coverTallUrls: normalizeMedia(book.coverTallUrls),
                    coverShortUrls: normalizeMedia(book.coverShortUrls),
                    featuredImageUrls: normalizeMedia(book.featuredImageUrls),
                    featuredMobileImageUrls: normalizeMedia(book.featuredMobileImageUrls),
                })) ?? [];

                await SaveText(dispatch, dataText.result.list);
                SetDebug("uiTexts returned " + new Date().toISOString());
                console.log("uiTexts returned " + new Date().toISOString());

                await SaveBooks(dispatch, normalizedBooks);
                SetDebug("books returned " + new Date().toISOString());
                console.log("books returned " + new Date().toISOString());

                await SaveTerms(dispatch, dataTerms.result.list.filter(item => item.taxonomyId === 1 || item.taxonomyId === 8));
                SetDebug("bulkPut returned " + new Date().toISOString());
                console.log("bulkPut returned " + new Date().toISOString());
                await updateLevelTermConnection(dispatch);
                SetDebug("updateLevelTermConnection " + new Date().toISOString());
                console.log("updateLevelTermConnection returned " + new Date().toISOString());
                await updateBookSearchString(dispatch);
                SetDebug("updateBookSearchString " + new Date().toISOString());
                console.log("updateBookSearchString returned " + new Date().toISOString());

                await SaveBadges(dispatch, dataBadges.result.list);
                SetDebug("badges returned " + new Date().toISOString());
                console.log("badges returned " + new Date().toISOString());

                // Setting the Geolocation and the Whitelisted Books (non-blocking for the rest of the UI)
                setGeolocation().catch(console.error);

                SetFetchDataState(2);
            } catch (error) {
                const message = error instanceof Error ? error.message : String(error);
                SetErrors(message);
            }
        };
        if (fetchDataState === 0 && !!languageToQuery && dispatch)
        {
            fetchData(languageToQuery).catch(console.error);
            SetFetchDataState(1);
        }
    }, [fetchDataState, languageToQuery, dispatch]);

    const isDev = (searchParams.get("dev") !== null) && (searchParams.get("dev") !== undefined)
    return (<div
        hidden={!(fetchDataState === 2) || !isDev}
        className={styles.Main}>
        {fetchDataState === 0 ? "Not Started" : fetchDataState === 1 ? "Loading" : "Finished"}{" "}{debug}{" "}{error}
    </div>)
}
